import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Building2, ChevronDown, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function OrgSwitcher() {
  const { activeOrganization, organizations, switchOrganization, clearOrganization } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Format role for display
  const getRoleDisplay = (role: string) => {
    const roleMap: Record<string, string> = {
      'ADMIN': 'Admin',
      'TUTOR': 'Tutor',
      'MEMBER': 'Member',
    };
    return roleMap[role] || role;
  };

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  if (organizations.length === 0) return null;

  const handleSwitch = async (orgId: string) => {
    setLoading(true);
    try {
      await switchOrganization(orgId);
    } catch (err) {
      console.error('Failed to switch organization:', err);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const handleClear = async () => {
    setLoading(true);
    try {
      await clearOrganization();
    } catch (err) {
      console.error('Failed to clear organization:', err);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="sm"
        className="flex items-center gap-1.5 px-2 text-sm font-medium max-w-[180px]"
        onClick={() => setOpen(v => !v)}
        disabled={loading}
      >
        {activeOrganization ? (
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate">
          {activeOrganization ? activeOrganization.name : 'Global'}
        </span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </Button>

      {open && (
        <div className="absolute left-0 mt-2 w-56 rounded-lg bg-popover shadow-lg border border-border z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs font-medium text-muted-foreground">Switch context</p>
          </div>

          <div className="py-1">
            {/* Global option */}
            <button
              className={`flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent w-full text-left ${
                !activeOrganization ? 'bg-accent font-medium' : ''
              }`}
              onClick={handleClear}
              disabled={loading}
            >
              <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>Global</span>
            </button>

            {/* Org options */}
            {organizations.map(org => (
              <button
                key={org.organizationId}
                className={`flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent w-full text-left ${
                  activeOrganization?.id === org.organizationId ? 'bg-accent font-medium' : ''
                }`}
                onClick={() => handleSwitch(org.organizationId)}
                disabled={loading}
              >
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="block truncate">{org.organizationName}</span>
                  <span className="block text-xs text-muted-foreground">
                    {getRoleDisplay(org.organizationRole)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
